import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import { EntityContext } from 'context/entityContext';
import GenericComponent, {
  Toolbar,
} from 'components/utility/GenericComponent/GenericComponent';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Controls from 'components/base/TableControls/TableControls';

import useMedia from 'hooks/useMedia';
import toggleOptions from 'maps/entity-types';
import getInitialSalesType from 'helpers/getInitialSalesType';
import salesTypes from 'maps/sales-types';
import pluralize from 'helpers/pluralize';
import EntityItemLoader from 'components/utility/ContentLoader/EntityItemLoader/EntityItemLoader';
import Table from 'components/data-visualization/Table/Table';
import Body from 'components/data-visualization/Table/Body';
import TableHead from 'components/data-visualization/Table/SortingHead';
import { TableRow, EntityCell } from './TableRow/TableRow.style';
import {
  FakeButtonWrapper,
  FakeButton,
  FakeControls,
  FakeDropdown,
  FakeSortToggle,
  FakeSortWrapper,
  FakeSearch,
  FakeTab,
  FakeTabsBlock,
} from 'components/base/SkeletonLoader/FakeInteractive';
import { RootState } from 'redux/rootReducer';

import {
  BrowseLayoutContainer,
  Label,
  ParentEntityItemLoader,
  SearchFieldBlock,
} from './shared/styles';
import { EntityTypes } from 'context/entityContext';
import TableBody from 'components/data-visualization/Table/Body';
import { TableHeader } from 'components/data-containers/BrowseEntities/shared/types';
import Layout from 'components/styled/Layout/Layout';
import Box from 'components/elements/Box';

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

export const StyledTableHead = styled(TableHead)`
  display: none;

  @media screen and (min-width: 50em) {
    display: table-header-group;
  }
`;

export const StyledEntityItemLoader = styled(EntityItemLoader)`
  padding: ${({ theme }): string => theme.spacing.small} 0 0;

  @media screen and (min-width: 50em) {
    padding: ${({ theme }): string => theme.spacing.small} 0;
  }
`;

const GenericComponentWrapper = styled('div')<any>`
  & > section {
    @media screen and (min-width: 40em) {
      margin: 0 auto;
    }
    @media screen and (min-width: 46.875em) and (max-width: 50em) {
      width: calc(100% - 2 * 1em);
    }
    @media screen and (min-width: 50em) {
      ${({ browseRelated }): string => (browseRelated ? 'width: 90%; ' : '')}
    }
    @media screen and (min-width: 60em) {
      width: ${({ browseRelated }): number => (browseRelated ? 90 : 78.2)}%;
      ${Layout.MaxWidth} {
        width: 100% !important;
      }
    }
    @media screen and (min-width: 80em) {
      ${({ browseRelated, isToolbarSpaced }): string =>
        browseRelated || isToolbarSpaced ? 'width: 75%;' : ''}
    }
  }
  ${AnimatedLoadingWrapper} {
    padding: 0 ${({ theme }): string => theme.spacing.small};
    @media screen and (min-width: 40em) {
      padding: 0 ${({ theme }): string => theme.spacing.xlarge};
    }
  }
  ${TableBody} {
    padding-right: 0;
  }
  ${Toolbar} {
    @media screen and (min-width: 40em) {
      padding: 2em ${({ theme }): string => theme.spacing.xlarge} 1em;
    }
  }
`;

const FakeSmallButton = styled(FakeButton)<any>`
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  padding: 0.75rem 1rem !important;
`;

/** The Skeleton loaders displaying 5 entity loaders. */
export const BrowseEntityItems = (): JSX.Element => (
  <>
    {Array(5)
      .fill(0)
      .map(
        (value: number, index: number): JSX.Element => (
          <TableRow key={`browse-entities-loader-${index}`} enableHover={false}>
            <EntityCell colSpan={4}>
              <StyledEntityItemLoader />
            </EntityCell>
          </TableRow>
        )
      )}
  </>
);

const headers = [
  {
    value: 'displayName',
    label: 'track name',
  },
  {
    value: 'performance_this_week_to_date',
    label: 'This week performance',
  },
  {
    value: 'performance_wow_in_progress',
    label: 'Week over week',
  },
  {
    value: 'performance_rtd',
    label: 'All time performance',
  },
];

interface IBrowseEntityLoaderProps {
  browseEntityType: EntityTypes | 'customList';
  enableSearch: boolean;
  isExternalUser: boolean;
  showToggle?: boolean | undefined;
  title: string;
  showVersions?: boolean | undefined;
  isBrowseTable?: boolean;
  short?: boolean;
  showTabs?: boolean;
  browseRelated?: boolean;
  isFakeSortToggle?: boolean;
  isNewDesign?: boolean;
  isNarrowScreen?: boolean;
}

const BrowseEntitiesLoader: React.FC<IBrowseEntityLoaderProps> = (props) => {
  const {
    browseEntityType,
    enableSearch,
    isExternalUser,
    title,
    showToggle,
    showVersions,
    isBrowseTable,
    short,
    showTabs = true,
    browseRelated,
    isFakeSortToggle = false,
    isNewDesign = false,
    isNarrowScreen,
  } = props;
  const { entityType, parentEntityId } = useContext(EntityContext);

  const sortBreakpoint = useMedia('(min-width: 50em)');
  const toggleBreakpoint = useMedia('(min-width: 40em)');

  const initialSalesType = useMemo(() => {
    return getInitialSalesType(isExternalUser, browseEntityType as EntityTypes);
  }, [isExternalUser, browseEntityType]);

  const isDropdownVisible =
    browseEntityType !== 'label' && browseEntityType !== 'customList';
  const headersToDisplay =
    browseEntityType === 'customList'
      ? headers.map((header: TableHeader) =>
          header.value === 'displayName'
            ? { value: 'displayName', label: 'Item name' }
            : header
        )
      : headers;
  const isTopLists = title.includes('Top ');

  const toggleSalesTypeOptions = [
    { value: 'album', label: 'Album Equivalents', active: false },
    { value: 'streams', label: 'Streams', active: true },
  ];

  return (
    <GenericComponentWrapper
      browseRelated={browseRelated}
      isTopLists={isTopLists}
    >
      <GenericComponent
        title={title}
        loading={true}
        success={false}
        showToolbarDropdown={false}
        short={short}
        browseRelated={browseRelated}
        isNewDesign={isNewDesign}
        isNarrowScreen={isNarrowScreen}
        isTabletNarrow={isTopLists}
      >
        <AnimatedLoadingWrapper loading={true} browseRelated={browseRelated}>
          {isBrowseTable ? (
            <FakeControls
              browseRelated={title.includes('Top ') ? false : browseRelated}
            >
              {enableSearch && (
                <SearchFieldBlock>
                  <FakeSearch>Search {pluralize(browseEntityType)}</FakeSearch>
                </SearchFieldBlock>
              )}
              {showTabs &&
                (showToggle && toggleBreakpoint ? (
                  <FakeTabsBlock>
                    {toggleOptions.map((option: any) => (
                      <FakeTab
                        key={option.value}
                        active={option.value === 'track'}
                      >
                        {option.label}
                      </FakeTab>
                    ))}
                  </FakeTabsBlock>
                ) : (
                  <FakeDropdown>{pluralize(browseEntityType)}</FakeDropdown>
                ))}
              {!sortBreakpoint && (
                <FakeSortWrapper isFakeSortToggle={isFakeSortToggle}>
                  <FakeDropdown>
                    {browseEntityType === 'customList'
                      ? 'Custom'
                      : 'This Week Performance'}
                  </FakeDropdown>
                  {!isFakeSortToggle && <FakeSortToggle />}
                </FakeSortWrapper>
              )}
              {isDropdownVisible &&
                (!sortBreakpoint ? (
                  <FakeDropdown>
                    {salesTypes[initialSalesType as string].label}
                  </FakeDropdown>
                ) : (
                  <Box display="flex" justifyContent="flex-end">
                    <FakeButtonWrapper>
                      {toggleSalesTypeOptions &&
                        toggleSalesTypeOptions.map((option: any) => (
                          <FakeSmallButton
                            key={option.value}
                            active={option.active}
                          >
                            {option.label}
                          </FakeSmallButton>
                        ))}
                    </FakeButtonWrapper>
                  </Box>
                ))}
            </FakeControls>
          ) : (
            <BrowseLayoutContainer>
              <Controls>
                {showToggle && toggleBreakpoint ? (
                  <FakeButtonWrapper>
                    {toggleOptions.map((option: any) => (
                      <FakeButton
                        key={option.value}
                        active={option.value === 'track'}
                      >
                        {option.label}
                      </FakeButton>
                    ))}
                  </FakeButtonWrapper>
                ) : (
                  <FakeDropdown>
                    Browse {pluralize(browseEntityType)}
                  </FakeDropdown>
                )}

                {browseEntityType !== 'label' && (
                  <FakeDropdown>
                    {salesTypes[initialSalesType as string].label}
                  </FakeDropdown>
                )}

                {!sortBreakpoint && (
                  <>
                    <FakeDropdown>This Week Performance</FakeDropdown>
                    <FakeSortToggle />
                  </>
                )}

                {enableSearch && (
                  <FakeSearch>Search {pluralize(browseEntityType)}</FakeSearch>
                )}
              </Controls>
              {showVersions && (
                <>
                  <Label>
                    This {parentEntityId ? 'version is part of' : entityType}
                  </Label>
                  <ParentEntityItemLoader />
                </>
              )}
            </BrowseLayoutContainer>
          )}
          <Table>
            <StyledTableHead
              handleClick={noop}
              columnHeaders={headersToDisplay}
              activeHeader={'performance_this_week_to_date'}
              order={'desc'}
            />
            <Body>
              <BrowseEntityItems />
            </Body>
          </Table>
        </AnimatedLoadingWrapper>
      </GenericComponent>
    </GenericComponentWrapper>
  );
};

export default connect((state: RootState): { isExternalUser: boolean } => ({
  isExternalUser: state.authentication.isExternalUser,
}))(BrowseEntitiesLoader);
